import {
  type MouseEventHandler,
  type ReactNode,
  forwardRef,
  useMemo,
  useState,
} from 'react';

import { Button } from '../../buttons/button/Button.js';
import { useAriaLabelingProps as _useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useId } from '../../core/hooks/useId.js';
import { useSafeForwardProps as _useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import {
  AriaDisabledProps,
  AriaLabelingProps,
} from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { DOMProps } from '../../core/types/dom.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { mergeProps } from '../../core/utils/merge-props.js';
import { Tooltip } from '../../overlays/tooltip/Tooltip.js';
import { useBaseInputContext } from './hooks/useBaseInputContext.js';

//#region Typings
/**
 * Accepted properties for the BaseInput Button
 * @public
 */
export interface BaseInputButtonProps
  extends
    AriaLabelingProps,
    AriaDisabledProps,
    DOMProps,
    StylingProps,
    DataTestId,
    BehaviorTrackingProps {
  /** Elements to be displayed in the BaseInput Button slot, preferably an icon or a similar visual element */
  children: ReactNode;
  /**
   * If a button is disabled e.g. it cannot be interacted with.
   * By default, it inherits the disabled prop of the BaseInput, but can be overwritten.
   * @defaultValue false
   */
  disabled?: boolean;
  /**
   * If specified, a tooltip is rendered with this text, which describes what the button does.
   * The text is also used as `aria-label` while the tooltip is closed, ensuring a valid accessible name at all times.
   * Please also consider translating the text in an internationalized context and use a short text description for the tooltip.
   * The positioning and placing of the according tooltip will be handled by the component.
   */
  tooltipText?: string;
  /**
   * Behavioral tracking props to forward exclusively to the tooltip overlay.
   * These will not be applied to the button itself.
   */
  tooltipBehavioralTrackingProps?: BehaviorTrackingProps;
  /** Called when the button is interacted with. */
  onClick?: MouseEventHandler;
  /**
   * Determines whether the input is in read-only mode.
   * @deprecated - readOnly is not a valid property on a button. Please use disabled if needed.
   */
  readOnly?: boolean;
}

//#endregion

/**
 * Component which allows users to trigger actions or events when clicking or when pressing `Enter` or `Space` while the button has focus.
 * @public
 */
export const BaseInputButton = /* @__PURE__ */ forwardRef<
  HTMLButtonElement,
  BaseInputButtonProps
>((props, forwardedRef) => {
  const {
    children,
    tooltipText,
    tooltipBehavioralTrackingProps: tooltipTrackingProps,
    disabled: propDisabled,
    id,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'aria-disabled': ariaDisabledProp,
    ...remainingProps
  }: BaseInputButtonProps = props;

  const [behavioralTrackingProps, restPropsWithoutTracking] =
    useBehavioralTrackingProps(remainingProps);

  const {
    hasError = false,
    disabled,
    variant,
    ariaDisabled: contextAriaDisabled,
  } = useBaseInputContext();
  const ariaDisabled = ariaDisabledProp || contextAriaDisabled;

  const tooltipGeneratedId = useId();
  const tooltipId = tooltipText ? (id ?? tooltipGeneratedId) : undefined;
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const {
    'aria-labelledby': ariaLabelledByProp,
    'aria-label': ariaLabelProp,
    ...remainingAriaProps
  } = _useAriaLabelingProps(restPropsWithoutTracking);

  const forwardProps = _useSafeForwardProps(restPropsWithoutTracking, {
    'aria-labelledby': ariaLabelledByProp,
    'aria-label': ariaLabelProp,
    ...remainingAriaProps,
  });

  // When tooltipText is the sole label source, aria-label provides the always-valid name.
  // aria-labelledby points to the tooltip element only while it is open (mounted in the DOM);
  // a dangling reference suppresses aria-label in some browsers.
  const ariaLabel =
    ariaLabelProp ??
    (tooltipText && !ariaLabelledByProp ? tooltipText : undefined);
  const ariaLabelledBy =
    ariaLabelledByProp ??
    (!ariaLabelProp && isTooltipOpen ? tooltipId : undefined);

  const button = useMemo(
    () => (
      <Button
        ref={forwardedRef}
        {...forwardProps}
        variant={variant === 'minimal' ? 'default' : 'emphasized'}
        disabled={propDisabled ?? disabled}
        aria-disabled={ariaDisabled}
        color={hasError && !disabled ? 'critical' : 'neutral'}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        className={consumerClassName}
        style={consumerStyle}
        data-testid={dataTestId}
        {...mergeProps(remainingAriaProps, behavioralTrackingProps)}
      >
        <Button.Prefix>{children}</Button.Prefix>
      </Button>
    ),
    [
      ariaLabel,
      ariaLabelledBy,
      ariaLabelProp,
      behavioralTrackingProps,
      children,
      consumerClassName,
      consumerStyle,
      dataTestId,
      disabled,
      forwardProps,
      forwardedRef,
      hasError,
      propDisabled,
      remainingAriaProps,
      variant,
      ariaDisabled,
    ],
  );

  return tooltipText ? (
    <Tooltip
      id={tooltipId}
      text={tooltipText}
      placement="top-end"
      {...tooltipTrackingProps}
      onOpenChange={setIsTooltipOpen}
    >
      {button}
    </Tooltip>
  ) : (
    button
  );
});

(
  BaseInputButton as typeof BaseInputButton & { displayName: string }
).displayName = 'BaseInput.Button';
